import {
  QBox,
  QButton,
  QDivider,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QRadio,
  QRadioGroup,
  QStack,
  QTag,
  QText,
} from '@qualio/ui-components';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCCWorkflowData } from '../../../../hooks/useCCWorkflowData';
import {
  CCWorkflowLink,
  ChangeControlTemplateDTO,
} from '../../../../types/DocumentCreate';
import { DocumentOverviewContext } from '../Context';

const SELECT_LATER_VALUE = 'select_later';

type CCWorkflowSelectionModalProps = {
  isDisabled?: boolean;
  canSelectLater?: boolean;
  onClose: () => void;
  onConfirm: (template: ChangeControlTemplateDTO | undefined) => void;
  confirmButtonText: string;
  title?: string;
};

export const ChangeControlSelectModal = ({
  isDisabled,
  canSelectLater,
  onClose,
  onConfirm,
  confirmButtonText,
  title,
}: CCWorkflowSelectionModalProps) => {
  const { t } = useTranslation();
  const modalTitle = title ?? t('createNewDraft.selectChangeControlTemplate');
  const { qualioDocument } = useContext(DocumentOverviewContext);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    null,
  );

  const { allCCTemplates, linkedCCWorkflows } = useCCWorkflowData(
    qualioDocument.company_id,
    qualioDocument.template_id,
    true,
  );

  const ccWorkflowOptions = useMemo(() => {
    const systemTemplate = allCCTemplates.find(
      (tmpl) => tmpl.qualio_managed && tmpl.status === 'effective',
    );

    const systemOption = systemTemplate
      ? [
          {
            label: systemTemplate.name ?? '',
            description: systemTemplate.description,
            value: String(systemTemplate.id),
            isDefault: true,
          },
        ]
      : [];

    const linkedOptions = (linkedCCWorkflows ?? [])
      .filter((wf: CCWorkflowLink) => !wf.qualio_managed)
      .map((wf: CCWorkflowLink) => ({
        label: wf.name,
        description: wf.description,
        value: wf.id.toString(),
        isDefault: false,
      }));

    return [...systemOption, ...linkedOptions];
  }, [linkedCCWorkflows, allCCTemplates]);

  // preselect the system managed template
  useEffect(() => {
    if (selectedWorkflowId) return;
    const defaultOption = ccWorkflowOptions.find((o) => o.isDefault);
    if (defaultOption) setSelectedWorkflowId(defaultOption.value);
  }, [ccWorkflowOptions, selectedWorkflowId]);

  const handleConfirm = () => {
    if (selectedWorkflowId === SELECT_LATER_VALUE) {
      return onConfirm(undefined);
    }
    const matchedTemplate = allCCTemplates.find(
      (t) => String(t.id) === selectedWorkflowId,
    );
    onConfirm(matchedTemplate);
  };

  return (
    <QModal size="xl" isOpen={true} onClose={onClose}>
      <QModalHeader>
        <QText>{modalTitle}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing={5}>
          <QText color="gray.500">{t('createNewDraft.chooseChangeType')}</QText>
          <QRadioGroup
            value={selectedWorkflowId ?? ''}
            onChange={setSelectedWorkflowId}
          >
            <QStack spacing={3}>
              {ccWorkflowOptions.map((option) => (
                <QBox
                  key={option.value}
                  border="1px"
                  borderColor={
                    selectedWorkflowId === option.value
                      ? 'blue.400'
                      : 'gray.200'
                  }
                  borderRadius="md"
                  p={4}
                  bg={selectedWorkflowId === option.value ? 'blue.50' : 'white'}
                >
                  <QRadio value={option.value}>
                    <QStack spacing={0} display="inline-flex">
                      <QStack direction="row" align="center" spacing={2}>
                        <QText fontWeight="medium">{option.label}</QText>
                        {option.isDefault && (
                          <QTag variantColor="blue">
                            {t('createNewDraft.default')}
                          </QTag>
                        )}
                      </QStack>
                      {option.description && (
                        <QText color="gray.500" fontSize="sm">
                          {option.description}
                        </QText>
                      )}
                    </QStack>
                  </QRadio>
                </QBox>
              ))}
              {canSelectLater && (
                <>
                  <QDivider />
                  <QRadio value={SELECT_LATER_VALUE}>
                    <QStack spacing={0}>
                      <QText fontWeight="medium">
                        {t('newDocument.ccSelectLater')}
                      </QText>
                      <QText color="gray.500" fontSize="sm">
                        {t('createNewDraft.selectLaterDescription')}
                      </QText>
                    </QStack>
                  </QRadio>
                </>
              )}
            </QStack>
          </QRadioGroup>
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton variant="outline" onClick={onClose}>
          {t('buttons.cancel')}
        </QButton>
        <QButton
          isDisabled={!selectedWorkflowId || isDisabled}
          onClick={handleConfirm}
        >
          {confirmButtonText}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
